import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, diagnoseNoResponse } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useDashboardMode } from '@/store/mode';
import { useTheme } from '@/store/theme';
import { lookupPostcode, type AddressSuggestion } from '@/lib/postcode';
import { MemberBadge } from '@/components/MemberBadge';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/dashboard/PageHeader';

interface Address { name?: string; line1: string; line2?: string; city: string; postcode: string; country: string; phone?: string }
interface ProfileData {
  name: string;
  email: string;
  phone?: string;
  defaultLockerCode?: string;
  payoutBankLast4?: string;
  twoFactorEnabled: boolean;
  deliveryAddress?: Address | null;
  billingAddress?: Address | null;
  shipFromAddress?: Address | null;
  idVerification?: 'PENDING' | 'APPROVED' | 'REJECTED';
  idDocumentSubmitted?: boolean;
  idVerifiedAt?: string | null;
  notifications: { email: boolean; sms: boolean; marketing: boolean };
}

export default function Profile() {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  // Active view (Buying / Selling). The "Switch to selling" invite belongs in
  // the Buying view only - never on a seller tab - so a seller is not nagged to
  // "become" what they already are. Computed identically to DashboardLayout.
  const storedMode = useDashboardMode((m) => m.mode);
  const storedUserId = useDashboardMode((m) => m.userId);
  const dashMode = (storedMode && storedUserId === user?.id) ? storedMode : 'SELLER';

  // Arriving from the dashboard "ID not verified" link (/dashboard/profile#identity)
  // drops the user straight at the verification section instead of the page top.
  useEffect(() => {
    if (location.hash !== '#identity') return;
    const t = setTimeout(() => {
      document.getElementById('identity')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(t);
  }, [location.hash]);
  const { data, refetch } = useQuery<ProfileData>({
    queryKey: ['profile'],
    queryFn: async () => (await api.get('/me/profile')).data,
  });
  // Live Stripe Connect status - this is the source of truth for payouts, not
  // payoutBankLast4. Hitting it also re-syncs the cached charges/payouts flags.
  const { data: connect, refetch: refetchConnect, isFetching: connectLoading } = useQuery<{
    required: boolean; connected: boolean; chargesEnabled?: boolean;
    payoutsEnabled: boolean; detailsSubmitted?: boolean; requirementsDue?: string[];
  }>({
    queryKey: ['connect-status'],
    queryFn: async () => (await api.get('/connect/status')).data,
    retry: false,
  });
  const { register, handleSubmit, formState: { isSubmitting } } = useForm<ProfileData>({ values: data });
  const [connecting, setConnecting] = useState(false);
  const setSession = useAuthStore((s) => s.setSession);
  const [switching, setSwitching] = useState(false);

  // Buyer → seller is a one-way, self-service upgrade - see backend/src/routes/me.ts.
  async function becomeSeller() {
    setSwitching(true);
    try {
      const { data: res } = await api.post('/me/become-seller');
      setSession(res.user, res.accessToken);
      toast.success('You can now sell on Souqli.');
      refetch();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Could not switch your account to selling.');
    } finally {
      setSwitching(false);
    }
  }

  // Stripe Connect onboarding - sends the seller to Stripe's hosted flow.
  async function connectPayouts() {
    // Open Stripe's hosted onboarding in a new tab so the profile page stays open behind it.
    const stripeTab = window.open('about:blank', '_blank');
    setConnecting(true);
    try {
      const { data: res } = await api.post('/connect/onboard');
      if (res?.url) {
        if (stripeTab) { stripeTab.location.href = res.url; }
        else { window.location.href = res.url; }
        toast.success('Stripe opened in a new tab. Come back here when you\u2019re done.');
        return;
      }
      if (stripeTab) stripeTab.close();
      toast.error('Could not start Stripe onboarding.');
    } catch (e: any) {
      if (stripeTab) stripeTab.close();
      toast.error(e?.response?.data?.message ?? 'Payout onboarding is unavailable until Stripe keys are configured.');
    } finally {
      setConnecting(false);
    }
  }

  async function onSubmit(values: ProfileData) {
    try {
      // Send only what this endpoint accepts. Posting the whole profile object
      // (email, addresses, verification state, notification prefs) meant one
      // unrelated field could fail validation and sink the whole save.
      await api.put('/me/profile', {
        name: values.name,
        phone: values.phone ?? '',
        defaultLockerCode: values.defaultLockerCode ?? '',
      });
      toast.success('Saved.');
      refetch();
    } catch (e: any) {
      if (!e?.response) return toast.error(await diagnoseNoResponse(), { duration: 9000 });
      const d = e.response.data;
      // Zod tells us the offending field - far better than "Could not save".
      const issue = d?.issues?.[0];
      toast.error(
        issue ? `${(issue.path ?? []).join('.') || 'Field'}: ${issue.message}`
              : d?.message ?? `Could not save (error ${e.response.status}).`,
        { duration: 9000 },
      );
    }
  }

  return (
    <div>
      <PageHeader
        eyebrow="PROFILE & SETTINGS"
        title="Your"
        accent="account."
        subtitle={<>Signed in as <span className="text-cream mono">{user?.email}</span></>}
      >
        <MemberBadge />
      </PageHeader>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6">
        {/* Identity card */}
        <Section title="Identity" stamp="01" desc="Public details on your seller card.">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Name"><input className="input" {...register('name')} /></Field>
            <Field label="Email"><input className="input" type="email" {...register('email')} /></Field>
            <Field label="Phone (for locker SMS)"><input className="input" {...register('phone')} /></Field>
            <Field label="Default locker postcode"><input className="input mono" placeholder="SW1A 1AA" {...register('defaultLockerCode')} /></Field>
          </div>
          {user?.role === 'BUYER' && dashMode === 'BUYER' && data?.idVerification !== 'APPROVED' && (
            <div className="mt-5 flex items-center justify-between gap-4 rounded-luxe border border-gold/40 bg-gold/5 px-4 py-3.5">
              <div>
                <div className="text-sm font-medium text-ink">Want to sell on Souqli too?</div>
                <div className="text-[12px] text-mute mt-0.5">Switch your account to selling to list items, and access Grow tools and Souqli Pro.</div>
              </div>
              <button type="button" onClick={becomeSeller} disabled={switching} className="btn btn-sm btn-gold shrink-0">
                {switching ? 'Switching…' : 'Switch to selling →'}
              </button>
            </div>
          )}
        </Section>

        {/* ID / KYC verification */}
        <Section id="identity" title="Identity verification" stamp="02" desc={user?.role === 'BUYER' ? 'Verify your ID to earn a verified badge and build trust with sellers.' : 'Verify your ID to earn the verified badge on your listings and unlock higher trust.'}>
          <IdVerification
            state={data?.idVerification}
            submitted={!!data?.idDocumentSubmitted}
            verifiedAt={data?.idVerifiedAt ?? null}
            audience={user?.role === 'BUYER' ? 'BUYER' : 'SELLER'}
            activePending={new URLSearchParams(location.search).get('idv') === 'complete'}
            onChange={refetch}
          />
        </Section>

        {/* Payout */}
        <Section title="Payouts" stamp="03" desc="Where your sales land. Connect Stripe to enable withdrawals - status refreshes live.">
          {(() => {
            // Source of truth = live Stripe status, not payoutBankLast4.
            const payoutsOn = !!connect?.payoutsEnabled;
            const connected = !!connect?.connected;
            const pending = connected && !payoutsOn; // onboarding started, Stripe still verifying
            const reqs = connect?.requirementsDue ?? [];
            return (
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <div className="flex-1 px-4 py-3 border border-warmline rounded-luxe bg-cream-deep">
                    {payoutsOn ? (
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">✅</span>
                        <div>
                          <div className="stamp text-ok">Payouts enabled</div>
                          <div className="font-medium">Your sales land here - withdraw any time{data?.payoutBankLast4 ? ` · bank ••••${data.payoutBankLast4}` : ''}.</div>
                        </div>
                      </div>
                    ) : pending ? (
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">⏳</span>
                        <div>
                          <div className="stamp text-warn">Connected - Stripe is verifying</div>
                          <div className="font-medium text-[13px]">Payouts switch on once Stripe finishes checking your details. This can take a little while after you submit.</div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-mute text-sm">No payout method connected yet. Connect Stripe to get paid.</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <button type="button" onClick={connectPayouts} disabled={connecting} className="btn btn-outline">
                      {connecting ? 'Opening…' : payoutsOn ? 'Manage on Stripe' : pending ? 'Finish on Stripe →' : 'Connect payouts →'}
                    </button>
                    {connected && (
                      <button type="button" onClick={() => refetchConnect()} disabled={connectLoading} className="btn btn-ghost btn-sm border border-warmline">
                        {connectLoading ? 'Checking…' : '↻ Refresh status'}
                      </button>
                    )}
                  </div>
                </div>
                {pending && reqs.length > 0 && (
                  <div className="text-[12px] text-mute rounded-luxe border border-warn/30 bg-warn/5 px-3.5 py-2.5">
                    Stripe still needs: <span className="text-ink">{reqs.map((r) => r.replace(/[._]/g, ' ')).join(', ')}</span>. Click “Finish on Stripe” to complete these.
                  </div>
                )}
              </div>
            );
          })()}
        </Section>

        {/* Addresses */}
        <Section title="Addresses" stamp="04" desc="Where couriered orders are delivered, and your billing address. Locker orders use your postcode.">
          {/* key forces a remount once the profile loads so the saved
              addresses populate the form (instead of the empty initial state). */}
          <Addresses key={data ? 'loaded' : 'loading'} delivery={data?.deliveryAddress} billing={data?.billingAddress} onSaved={refetch} />
        </Section>

        {/* Ship-from & drop-off - sellers only. Private, never shown to buyers. */}
        {(user?.role === 'SELLER' || user?.role === 'ADMIN') && (
          <Section title="Ship-from & drop-off" stamp="4b" desc="Where you send parcels from, and your nearest drop-off locker. Kept private - buyers never see this; Souqli's address is printed as the sender.">
            <ShipFrom
              key={data ? 'sf-loaded' : 'sf-loading'}
              shipFrom={data?.shipFromAddress}
              lockerCode={data?.defaultLockerCode}
              onSaved={refetch}
            />
          </Section>
        )}

        {/* Security */}
        <Section title="Security" stamp="05" desc="Lock down your account against takeover attempts.">
          <TwoFactor enabled={!!data?.twoFactorEnabled} onChange={refetch} />
        </Section>

        {/* Notifications */}
        <Section title="Notifications" stamp="06" desc="Choose how Souqli pings you about activity.">
          <div className="grid sm:grid-cols-3 gap-3">
            <Toggle reg={register('notifications.email')}      label="Email"            desc="Order updates, offers, payouts." />
            <Toggle reg={register('notifications.sms')}        label="SMS"              desc="Locker PINs (highly recommended)." />
            <Toggle reg={register('notifications.marketing')}  label="Curated weekly"   desc="Hand-picked drops on Sundays." />
          </div>
        </Section>

        <Section title="Appearance" stamp="07" desc="Choose how Souqli looks on this device. Day Souq is light; Night Souq is dark. Saved on this device.">
          <ThemeChoice />
        </Section>

        <div className="flex justify-end gap-3 pt-4">
          <button type="button" className="btn btn-ghost border border-warmline">Cancel</button>
          <button disabled={isSubmitting} className="btn btn-primary btn-magnetic">
            {isSubmitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

function ThemeChoice() {
  const theme = useTheme((t) => t.theme);
  const setTheme = useTheme((t) => t.setTheme);
  const opts: { v: 'day' | 'night'; label: string; desc: string }[] = [
    { v: 'day',   label: 'Day Souq',   desc: 'Light — warm sand & saffron' },
    { v: 'night', label: 'Night Souq', desc: 'Dark — deep espresso & gold' },
  ];
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {opts.map((o) => {
        const active = theme === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => setTheme(o.v)}
            aria-pressed={active}
            className={`text-left p-4 border rounded-luxe transition flex items-start gap-3 ${active ? 'border-gold bg-gold-soft shadow-glow' : 'border-warmline hover:border-gold'}`}
          >
            <span className={`mt-0.5 ${active ? 'text-gold-dark' : 'text-mute'}`}>
              {o.v === 'day' ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>
              )}
            </span>
            <div>
              <div className="text-sm font-medium flex items-center gap-2">{o.label}{active && <span className="pill pill-ok text-[9px]">Selected</span>}</div>
              <div className="text-[11px] text-mute mt-0.5">{o.desc}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function Section({ stamp, title, desc, children, id }: { stamp: string; title: string; desc: string; children: React.ReactNode; id?: string }) {
  return (
    <div id={id} className="card p-7 grid lg:grid-cols-12 gap-6 scroll-mt-24">
      <div className="lg:col-span-4">
        <div className="stamp text-gold-dark">{stamp}</div>
        <h2 className="display text-2xl mt-1">{title}</h2>
        <p className="text-xs text-mute mt-2 leading-relaxed max-w-[36ch]">{desc}</p>
      </div>
      <div className="lg:col-span-8">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

function Toggle({ reg, label, desc }: { reg: any; label: string; desc: string }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer p-4 border border-warmline rounded-luxe hover:border-gold transition">
      <input type="checkbox" className="mt-1 accent-gold" {...reg} />
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="stamp text-mute mt-1">{desc}</div>
      </div>
    </label>
  );
}

const EMPTY_ADDRESS: Address = { line1: '', city: '', postcode: '', country: 'United Kingdom' };

function Addresses({ delivery, billing, onSaved }: { delivery?: Address | null; billing?: Address | null; onSaved: () => void }) {
  const [d, setD] = useState<Address>(delivery ?? EMPTY_ADDRESS);
  const [b, setB] = useState<Address>(billing ?? EMPTY_ADDRESS);
  const [same, setSame] = useState(!billing);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!d.line1 || !d.city || !d.postcode) { toast.error('Please fill in address line 1, city and postcode.'); return; }
    if (!same && (!b.line1 || !b.city || !b.postcode)) { toast.error('Complete the billing address, or tick “same as delivery”.'); return; }
    setSaving(true);
    try {
      await api.put('/me/addresses', { deliveryAddress: d, billingAddress: same ? d : b });
      toast.success('Addresses saved.');
      onSaved();
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? 'Could not save addresses.';
      // A 500 here almost always means the deliveryAddress/billingAddress columns
      // aren't in the database yet - surface it so it's actionable.
      toast.error(/internal server error/i.test(msg)
        ? 'Server could not save - run "npx prisma db push" in the backend, then restart it.'
        : msg);
    }
    finally { setSaving(false); }
  }

  return (
    <div className="space-y-5">
      <div>
        <div className="stamp text-gold-dark text-[9px] mb-2">DELIVERY ADDRESS</div>
        <AddressForm value={d} onChange={setD} />
      </div>

      <label className="flex items-center gap-2.5 text-sm cursor-pointer">
        <input type="checkbox" className="accent-gold" checked={same} onChange={(e) => setSame(e.target.checked)} />
        Billing address is the same as delivery
      </label>

      {!same && (
        <div>
          <div className="stamp text-gold-dark text-[9px] mb-2">BILLING ADDRESS</div>
          <AddressForm value={b} onChange={setB} />
        </div>
      )}

      <button type="button" onClick={save} disabled={saving} className="btn btn-primary btn-sm">
        {saving ? 'Saving…' : 'Save addresses'}
      </button>
    </div>
  );
}

/**
 * Seller ship-from base + preferred drop-off locker.
 *
 * Private by design: this is never printed on a buyer-facing label (Souqli's
 * own address is the sender). It's used internally to route the seller's
 * drop-off and to find their nearest locker.
 */
function ShipFrom({ shipFrom, lockerCode, onSaved }: { shipFrom?: Address | null; lockerCode?: string; onSaved: () => void }) {
  const [addr, setAddr] = useState<Address>(shipFrom ?? EMPTY_ADDRESS);
  const [locker, setLocker] = useState(lockerCode ?? '');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!addr.line1 || !addr.city || !addr.postcode) {
      toast.error('Please fill in address line 1, city and postcode.');
      return;
    }
    setSaving(true);
    try {
      await api.put('/me/ship-from', { shipFromAddress: addr, defaultLockerCode: locker });
      toast.success('Ship-from saved.');
      onSaved();
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? 'Could not save ship-from.';
      toast.error(/internal server error/i.test(msg)
        ? 'Server could not save - run "npx prisma migrate deploy" in the backend, then restart it.'
        : msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-2.5 rounded-luxe border border-sage/30 bg-sage/5 px-3.5 py-3">
        <span className="text-sage-dark mt-0.5">🔒</span>
        <p className="text-[12px] text-mute leading-relaxed">
          Private to you. Buyers never see your address - Souqli is printed as the sender on every label, and
          locker orders exchange no address at all.
        </p>
      </div>

      <div>
        <div className="stamp text-gold-dark text-[9px] mb-2">SHIP-FROM ADDRESS</div>
        <AddressForm value={addr} onChange={setAddr} />
      </div>

      <div>
        <label className="label">Preferred drop-off locker / postcode</label>
        <input
          className="input mono"
          placeholder="SW1A 1AA"
          value={locker}
          onChange={(e) => setLocker(e.target.value)}
        />
        <p className="stamp text-mute mt-1.5">We use this to suggest your nearest InPost locker at drop-off.</p>
      </div>

      <button type="button" onClick={save} disabled={saving} className="btn btn-primary btn-sm">
        {saving ? 'Saving…' : 'Save ship-from'}
      </button>
    </div>
  );
}

/** Identity (KYC) verification via Didit - embedded inline (document + liveness + face-match). */
function IdVerification({ state, verifiedAt, audience = 'SELLER', activePending = false }: {
  state?: 'PENDING' | 'APPROVED' | 'REJECTED';
  submitted?: boolean;
  verifiedAt?: string | null;
  audience?: 'BUYER' | 'SELLER';
  onChange?: () => void;
  activePending?: boolean;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  // Didit session URL, shown inline in an iframe rather than redirecting away.
  const [sessionUrl, setSessionUrl] = useState<string | null>(null);
  // Set once the user finishes the inline flow, before the webhook result lands.
  const [submittedInline, setSubmittedInline] = useState(false);

  async function startVerify() {
    setBusy(true);
    try {
      const { data } = await api.post('/me/id-verification/start');
      if (data?.url) { setSessionUrl(data.url as string); setBusy(false); return; }
      toast.error('Could not start verification. Please try again.');
      setBusy(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Could not start verification.');
      setBusy(false);
    }
  }

  // Didit returns the user to our callback (.../dashboard/profile?idv=complete)
  // when the flow finishes. Inside the iframe that lands back on our own origin,
  // so we can read the frame URL on load; while the user is still on the
  // partner's pages the read throws (cross-origin) and we ignore it.
  function onFrameLoad(e: React.SyntheticEvent<HTMLIFrameElement>) {
    try {
      const href = e.currentTarget.contentWindow?.location?.href ?? '';
      if (href && (href.includes('idv=complete') || href.includes('/dashboard/profile'))) {
        setSessionUrl(null);
        setSubmittedInline(true);
        qc.invalidateQueries({ queryKey: ['profile'] });
        toast.success('Thanks - we are confirming your identity now.');
      }
    } catch { /* still on the verification partner's page */ }
  }

  if (state === 'APPROVED') {
    return (
      <div className="flex items-center gap-3 rounded-luxe border border-ok/40 bg-ok/8 px-4 py-3">
        <span className="w-9 h-9 rounded-full bg-ok text-purewhite grid place-items-center text-lg">✓</span>
        <div>
          <div className="font-medium text-ink">Identity verified</div>
          <div className="text-[12px] text-mute">{verifiedAt ? `Verified on ${new Date(verifiedAt).toLocaleDateString()}` : (audience === 'BUYER' ? 'Sellers can see you are a verified buyer.' : 'Your verified badge is live on your listings.')}</div>
        </div>
      </div>
    );
  }

  // Inline verification: Didit's flow runs in-page, under the heading above.
  if (sessionUrl) {
    return (
      <div className="rounded-luxe border border-warmline bg-cream-deep p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="font-medium text-ink">Verify your identity</div>
          <button onClick={() => setSessionUrl(null)} className="text-[12px] text-mute hover:text-ink">Cancel</button>
        </div>
        <p className="text-[12px] text-mute">
          Have your passport or driving licence ready, and allow camera access when asked. The steps run right here, so you will not leave Souqli.
        </p>
        <iframe
          src={sessionUrl}
          title="Identity verification"
          allow="camera; microphone; fullscreen; autoplay; encrypted-media"
          className="w-full rounded-luxe border border-warmline bg-white"
          style={{ height: 'min(80vh, 640px)' }}
          onLoad={onFrameLoad}
        />
        <p className="text-[11px] text-mute">
          Trouble with the camera on your phone?{' '}
          <a href={sessionUrl} target="_blank" rel="noopener noreferrer" className="text-gold-dark underline">Open the check in a new tab</a>.
        </p>
      </div>
    );
  }

  if (submittedInline || (state === 'PENDING' && activePending)) {
    return (
      <div className="flex items-center gap-3 rounded-luxe border border-warn/40 bg-warn/8 px-4 py-3">
        <span className="w-9 h-9 rounded-full bg-warn text-purewhite grid place-items-center text-base">⏳</span>
        <div className="flex-1">
          <div className="font-medium text-ink">Verification in progress</div>
          <div className="text-[12px] text-mute">We are confirming your identity - this usually takes under a minute. Refresh to see the result.</div>
        </div>
        <button onClick={startVerify} disabled={busy} className="btn btn-sm btn-outline">{busy ? 'Starting...' : 'Restart'}</button>
      </div>
    );
  }

  const rejected = state === 'REJECTED';
  return (
    <div className={`rounded-luxe border px-4 py-4 ${rejected ? 'border-bad/40 bg-bad/5' : 'border-warmline bg-cream-deep'}`}>
      <div className="flex items-start gap-3">
        <span className="text-2xl">{rejected ? '⚠️' : '🪩'}</span>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-ink">{rejected ? 'Verification unsuccessful' : 'Verify your identity'}</div>
          <div className="text-[12px] text-mute mt-0.5">
            {rejected
              ? 'We could not verify your last attempt. Make sure your account name matches your ID, then try again.'
              : 'A quick, secure check: a photo of your passport or driving licence and a selfie. Your document is handled by our verification partner and never shown publicly.'}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button onClick={startVerify} disabled={busy} className="btn btn-sm btn-primary">{busy ? 'Starting...' : (rejected ? 'Try again' : 'Verify my identity')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddressForm({ value, onChange }: { value: Address; onChange: (a: Address) => void }) {
  const set = (k: keyof Address, v: string) => onChange({ ...value, [k]: v });
  const [looking, setLooking] = useState(false);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);

  const runLookup = async () => {
    if (!value.postcode || value.postcode.trim().length < 5) { toast.error('Enter a full postcode first.'); return; }
    setLooking(true);
    const r = await lookupPostcode(value.postcode);
    setLooking(false);
    setSuggestions([]);
    if (!r) { toast('Postcode not found - add your address manually.', { icon: '✍️' }); return; }

    // Always fill what we know; the picker refines it if we have one.
    onChange({ ...value, city: r.city ?? value.city, country: r.country, postcode: r.postcode });

    if (r.suggestions.length) {
      setSuggestions(r.suggestions);
      toast.success(`${r.suggestions.length} addresses found - pick yours.`);
    } else {
      // Street-level data is Royal Mail's PAF and needs a licensed provider,
      // so on the free tier we say what we could do rather than failing.
      toast(`${r.city ?? 'Area'} found - add your house number and street.`, { icon: '✍️' });
    }
  };

  const pick = (a: AddressSuggestion) => {
    onChange({ ...value, line1: a.line1, line2: a.line2 ?? '', city: a.city || value.city });
    setSuggestions([]);
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="col-span-1 sm:col-span-2"><label className="label">Full name</label><input className="input" value={value.name ?? ''} onChange={(e) => set('name', e.target.value)} placeholder="Recipient name" /></div>
      {/* Postcode first: look it up to auto-fill city + country, or fill it all in manually. */}
      <div className="col-span-2">
        <label className="label">Postcode</label>
        <div className="flex gap-2">
          <input className="input mono uppercase flex-1" value={value.postcode} onChange={(e) => set('postcode', e.target.value)}
            onBlur={() => { if (value.postcode && !value.city) runLookup(); }} placeholder="SW1A 1AA" />
          <button type="button" onClick={runLookup} disabled={looking} className="btn btn-outline shrink-0 whitespace-nowrap">
            {looking ? 'Finding…' : 'Find address'}
          </button>
        </div>

        {/* Pick-list. Capped and scrollable - a big postcode can return 100+
            addresses and an unbounded list buries the rest of the form. */}
        {!!suggestions.length && (
          <div className="mt-2 border border-gold/40 rounded-luxe bg-gold/5 max-h-56 overflow-y-auto">
            <div className="stamp text-[9px] text-gold-dark px-3 pt-2.5 pb-1">
              {suggestions.length} ADDRESSES AT THIS POSTCODE
            </div>
            {suggestions.map((a, i) => (
              <button
                key={i}
                type="button"
                onClick={() => pick(a)}
                className="w-full text-left px-3 py-2 text-[13px] hover:bg-gold/10 border-t border-gold/15 transition"
              >
                {a.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setSuggestions([])}
              className="w-full text-left px-3 py-2 text-[12px] text-mute hover:bg-gold/10 border-t border-gold/15"
            >
              None of these - I'll type it myself
            </button>
          </div>
        )}
      </div>
      <div className="col-span-2"><label className="label">Address line 1</label><input className="input" value={value.line1} onChange={(e) => set('line1', e.target.value)} placeholder="House number and street" /></div>
      <div className="col-span-2"><label className="label">Address line 2 <span className="text-mute font-normal lowercase">- optional</span></label><input className="input" value={value.line2 ?? ''} onChange={(e) => set('line2', e.target.value)} placeholder="Flat, building, etc." /></div>
      <div><label className="label">City / town</label><input className="input" value={value.city} onChange={(e) => set('city', e.target.value)} placeholder="London" /></div>
      <div><label className="label">Country</label><input className="input" value={value.country} onChange={(e) => set('country', e.target.value)} /></div>
      <div className="col-span-2"><label className="label">Phone <span className="text-mute font-normal lowercase">- for delivery</span></label><input className="input" value={value.phone ?? ''} onChange={(e) => set('phone', e.target.value)} placeholder="+44…" /></div>
    </div>
  );
}

function TwoFactor({ enabled, onChange }: { enabled: boolean; onChange: () => void }) {
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  async function startSetup() {
    setBusy(true);
    try { const { data } = await api.post('/me/2fa/setup'); setSetup(data); setCode(''); }
    catch { toast.error('Could not start 2FA setup.'); }
    finally { setBusy(false); }
  }
  async function confirm() {
    setBusy(true);
    try { await api.post('/me/2fa/enable', { code }); toast.success('Two-factor authentication enabled.'); setSetup(null); onChange(); }
    catch (e: any) { toast.error(e?.response?.data?.message ?? 'Could not enable 2FA.'); }
    finally { setBusy(false); }
  }
  async function disable() {
    setBusy(true);
    try { await api.post('/me/2fa/disable'); toast.success('Two-factor authentication turned off.'); onChange(); }
    catch { toast.error('Could not disable 2FA.'); }
    finally { setBusy(false); }
  }

  if (enabled) {
    return (
      <div className="p-4 border border-ok/40 bg-ok/5 rounded-luxe flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium flex items-center gap-2"><span className="pill pill-ok text-[9px]">ON</span> Two-factor authentication</div>
          <div className="stamp text-mute mt-1">Your account is protected with an authenticator app.</div>
        </div>
        <button type="button" onClick={disable} disabled={busy} className="btn btn-sm btn-outline">Turn off</button>
      </div>
    );
  }

  return (
    <div className="p-4 border border-warmline rounded-luxe">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium">Two-factor authentication (TOTP)</div>
          <div className="stamp text-mute mt-1">Use Google Authenticator, Authy or 1Password.</div>
        </div>
        {!setup && <button type="button" onClick={startSetup} disabled={busy} className="btn btn-sm btn-primary">{busy ? 'Starting…' : 'Enable 2FA'}</button>}
      </div>

      {setup && (
        <div className="mt-4 grid sm:grid-cols-[180px_1fr] gap-5 items-start border-t border-warmline pt-4">
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(setup.otpauthUrl)}`}
            alt="Scan this QR in your authenticator app"
            width={180} height={180}
            className="rounded-luxe border border-warmline shrink-0"
          />
          <div>
            <div className="text-[13px] text-ink-700">1. Scan the QR in your authenticator app, or enter this key manually:</div>
            <div className="mono text-[12px] bg-cream-deep border border-warmline rounded px-2 py-1.5 mt-2 break-all">{setup.secret}</div>
            <div className="text-[13px] text-ink-700 mt-4">2. Enter the current 6-digit code to confirm:</div>
            <div className="flex flex-wrap gap-2 mt-2">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                inputMode="numeric"
                className="input mono w-32 text-center tracking-[0.3em]"
              />
              <button type="button" onClick={confirm} disabled={busy || code.length !== 6} className="btn btn-sm btn-primary">{busy ? 'Verifying…' : 'Verify & enable'}</button>
              <button type="button" onClick={() => setSetup(null)} className="btn btn-sm btn-ghost text-mute">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
